import { AlertTriangle } from "lucide-react";
import {
  PERFORMANCE_ISSUE_DESCRIPTIONS,
  type PerformanceIssueType,
} from "../../../../../models/audit/attributeTestColumn";
import { PaneSection } from "./PaneSection";

interface ConclusionSectionProps {
  /** The verdict's own account of what it concluded. */
  prose: string | null;
  /**
   * The composed sentence carrying the run's figures.
   *
   * Rendered exactly as it arrives. The figures inside it are composed
   * server-side from the stored result row and the manifest, and a second
   * composition here would be a second sentence free to disagree with the one
   * the export writes.
   */
  figures: string | null;
  mitigatingFactors: string | null;
  performanceIssues: PerformanceIssueType[];
  /**
   * The step a run that published nothing stopped at, or null.
   *
   * Both halves are the caller's to establish, and it withholds the name unless
   * each is read from the source that owns it: a run known to have stopped at
   * that step, and a publication list known to be empty. Null wherever either is
   * merely unread, because this callout states the run's outcome and an unread
   * fact states nothing.
   */
  failedStepName: string | null;
}

export function ConclusionSection({
  prose,
  figures,
  mitigatingFactors,
  performanceIssues,
  failedStepName,
}: ConclusionSectionProps) {
  const hasContent =
    prose !== null ||
    figures !== null ||
    mitigatingFactors !== null ||
    performanceIssues.length > 0 ||
    failedStepName !== null;

  if (!hasContent) return null;

  return (
    <PaneSection title="CONCLUSION">
      {prose !== null && (
        <p className="text-sm whitespace-pre-wrap break-words">{prose}</p>
      )}

      {figures !== null && (
        <div className="rounded-md bg-stone-100 px-3 py-2 text-sm">
          <p className="whitespace-pre-wrap break-words">{figures}</p>
        </div>
      )}

      {failedStepName !== null && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The run stopped at {failedStepName} and published nothing.
          </span>
        </div>
      )}

      {/* The verdict's remaining prose, kept in this section because it is part
          of the conclusion's account and the pane has no sixth heading to put it
          under. Read-only: an analytics conclusion is replaced by minting a
          superseding verdict, never by editing the one on screen. */}
      {performanceIssues.length > 0 && (
        <div className="text-sm">
          <div className="font-mono text-xs text-font-secondary">
            Why testing did not conclude
          </div>
          <p className="whitespace-pre-wrap break-words">
            {performanceIssues
              .map((issue) => PERFORMANCE_ISSUE_DESCRIPTIONS[issue])
              .join("\n")}
          </p>
        </div>
      )}

      {mitigatingFactors !== null && (
        <div className="text-sm">
          <div className="font-mono text-xs text-font-secondary">
            Potential mitigating factors
          </div>
          <p className="whitespace-pre-wrap break-words">{mitigatingFactors}</p>
        </div>
      )}
    </PaneSection>
  );
}
